import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Loader2, Mail, Sparkles, Lock } from "lucide-react";
import { useAuth, startGoogleLogin } from "@/auth";
import { Seo } from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const TOPICS = {
  "custom-plan": {
    kind: "custom_plan",
    title: "Custom Plan enquiry.",
    body: "Tell us about your business and the tasks eating your time — we'll tailor a plan and price around your exact needs.",
    prefill: "Hi DISJINX — I'd like to enquire about a Custom Plan for my business.\n\nThe repetitive tasks eating our time include: ",
    isPlan: true,
  },
  "free-trial": {
    kind: "free_trial",
    title: "Free Trial enquiry.",
    body: "Tell us a little about your business and one repetitive task — we'll build a simple custom automation and run it free for one week, no charge and no commitment.",
    prefill: "Hi DISJINX — I'd like to try the free one-week trial.\n\nA repetitive task eating our time is: ",
  },
  "plan-essential": {
    kind: "plan_essential",
    title: "Essential Plan enquiry.",
    body: "Tell us what repetitive tasks are taking your time, and/or what you'd like to automate — we'll take it from there.",
    prefill: "",
    isPlan: true,
  },
  "plan-advanced": {
    kind: "plan_advanced",
    title: "Advanced Plan enquiry.",
    body: "Tell us what repetitive tasks are taking your time, and/or what you'd like to automate — we'll take it from there.",
    prefill: "",
    isPlan: true,
  },
  "plan-pro": {
    kind: "plan_pro",
    title: "Pro Plan enquiry.",
    body: "Tell us what repetitive tasks are taking your time, and/or what you'd like to automate — we'll take it from there.",
    prefill: "",
    isPlan: true,
  },
};

export default function Contact() {
  const [params] = useSearchParams();
  const topic = TOPICS[params.get("topic")] || null;
  const blank = {
    name: "",
    email: "",
    business_name: "",
    message: topic ? topic.prefill : "",
  };
  const [form, setForm] = useState(blank);
  const [sending, setSending] = useState(false);
  const { user, loading: authLoading } = useAuth();

  useEffect(() => {
    if (user && topic?.isPlan) {
      setForm((f) => ({
        ...f,
        email: f.email || user.email,
        name: f.name || user.name || "",
      }));
    }
  }, [user, topic]);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    if (topic?.isPlan && !user) {
      startGoogleLogin();
      return;
    }
    setSending(true);
    try {
      const payload = { ...form, kind: topic ? topic.kind : "business" };
      if (!payload.business_name) delete payload.business_name;
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to send");
      toast.success("Enquiry sent — we'll be in touch soon.");
      setForm({ name: "", email: "", business_name: "", message: topic ? topic.prefill : "" });
    } catch (err) {
      toast.error(err.message || "Something went wrong. Please email disjinx.support@gmail.com directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-page" className="pt-40 pb-32 lg:pt-52">
      <Seo
        title="Contact DISJINX | AI Automation Agency Australia"
        description="Tell DISJINX what repetitive tasks are eating your week. AI automation for Australian businesses — enquire about plans from $49/week or book a free one-week trial."
        path="/contact"
      />
      <div className="mx-auto grid max-w-7xl gap-20 px-6 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-5">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="mb-8 text-xs font-semibold uppercase tracking-[0.25em] text-neutral-500"
          >
            Contact DISJINX
          </motion.p>
          <h1 data-testid="contact-headline" className="font-display text-5xl font-extrabold leading-[1.02] tracking-tight lg:text-6xl">
            <MaskedLine delay={0.15}>Tell us what's</MaskedLine>
            <MaskedLine delay={0.28} className="text-neutral-500">eating your time.</MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 max-w-md text-base leading-relaxed text-neutral-600 md:text-lg"
          >
            Fill out the short form and your enquiry lands straight in our inbox.
            We read everything, and we reply fast.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65, ease: [0.16, 1, 0.3, 1] }}
            className="mt-12 space-y-4"
          >
            <div className="flex items-center gap-3 text-sm text-neutral-600">
              <Mail className="h-4 w-4 text-neutral-950" />
              <span data-testid="contact-direct-email">disjinx.support@gmail.com</span>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-7"
        >
          {topic?.isPlan && authLoading ? (
            <div className="flex h-64 items-center justify-center border border-neutral-200 bg-white">
              <Loader2 className="h-8 w-8 animate-spin text-neutral-400" />
            </div>
          ) : topic?.isPlan && !user ? (
            <div data-testid="plan-signin-required" className="border border-neutral-200 bg-white p-10 lg:p-14">
              <Lock className="h-8 w-8 text-neutral-950" strokeWidth={1.5} />
              <h2 className="mt-6 font-display text-2xl font-bold tracking-tight">Sign in to enquire</h2>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-neutral-600">
                Plan enquiries need a free DISJINX account — it links the plan to you, so once your automation
                is agreed and running you can see your plan and manage it from your My Billing page.
              </p>
              <button
                data-testid="plan-signin-button"
                onClick={startGoogleLogin}
                className="mt-8 inline-flex items-center gap-2 bg-neutral-950 px-7 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-neutral-800"
              >
                Sign in with Google
                <ArrowRight className="h-4 w-4" />
              </button>
              <p className="mt-4 text-xs text-neutral-500">One click with your Google account — no passwords.</p>
            </div>
          ) : (
          <form data-testid="contact-form" onSubmit={submit} className="border border-neutral-200 bg-white p-8 lg:p-12">
            {topic && (
              <div data-testid="topic-notice" className="mb-10 flex items-start gap-3 border border-neutral-950 bg-neutral-950 p-5 text-white">
                <Sparkles className="mt-0.5 h-4 w-4 shrink-0" />
                <p className="text-sm leading-relaxed">
                  <strong className="font-semibold">{topic.title}</strong> {topic.body}
                </p>
              </div>
            )}
            <div className="space-y-10">
              <div>
                <label htmlFor="contact-name" className="mb-3 block text-xs font-semibold uppercase tracking-widest text-neutral-500">
                  Your name
                </label>
                <input
                  id="contact-name"
                  data-testid="contact-name-input"
                  required
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Jane Citizen"
                  className="w-full border-b border-neutral-300 bg-transparent py-3 text-base outline-none transition-colors duration-200 placeholder:text-neutral-500 focus:border-neutral-950"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="mb-3 block text-xs font-semibold uppercase tracking-widest text-neutral-500">
                  Your email
                </label>
                <input
                  id="contact-email"
                  data-testid="contact-email-input"
                  type="email"
                  required
                  value={form.email}
                  onChange={update("email")}
                  placeholder="jane@gmail.com"
                  className="w-full border-b border-neutral-300 bg-transparent py-3 text-base outline-none transition-colors duration-200 placeholder:text-neutral-500 focus:border-neutral-950"
                />
              </div>
              {topic?.isPlan && (
                <div>
                  <label htmlFor="contact-business" className="mb-3 block text-xs font-semibold uppercase tracking-widest text-neutral-500">
                    Business name <span className="normal-case tracking-normal text-neutral-500">(optional)</span>
                  </label>
                  <input
                    id="contact-business"
                    data-testid="contact-business-input"
                    value={form.business_name}
                    onChange={update("business_name")}
                    placeholder="Acme Pty Ltd"
                    className="w-full border-b border-neutral-300 bg-transparent py-3 text-base outline-none transition-colors duration-200 placeholder:text-neutral-500 focus:border-neutral-950"
                  />
                </div>
              )}
              <div>
                <label htmlFor="contact-message" className="mb-3 block text-xs font-semibold uppercase tracking-widest text-neutral-500">
                  {topic?.isPlan
                    ? "What repetitive tasks are taking your time, and/or what would you like to automate?"
                    : "What would you like to say or enquire about?"}
                </label>
                <textarea
                  id="contact-message"
                  data-testid="contact-message-input"
                  required
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  placeholder={topic?.isPlan ? "Every week we spend hours on…" : "We spend hours every week on…"}
                  className="w-full resize-none border-b border-neutral-300 bg-transparent py-3 text-base outline-none transition-colors duration-200 placeholder:text-neutral-500 focus:border-neutral-950"
                />
              </div>
              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={sending}
                className="group inline-flex items-center gap-2 bg-neutral-950 px-7 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-neutral-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {sending ? (
                  <>
                    Sending
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </>
                ) : (
                  <>
                    Send enquiry
                    <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                  </>
                )}
              </button>
            </div>
          </form>
          )}
        </motion.div>
      </div>
    </main>
  );
}
